/**
 * NewWorkspaceWizard
 *
 * Inline 3-step wizard that replaces the workspace list content.
 * No modal — the parent page slides the list out and slides this in.
 *
 * Step 1 — Name & Description
 * Step 2 — Jurisdictions (Browse tree + Search)
 * Step 3 — Focus (date range + topic tags) + summary card
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronRight,
  FileText,
  Globe,
  Loader2,
  MapPin,
  Plus,
  Search,
  X,
  Building2,
} from 'lucide-react';
import { apiFetch } from '@/lib/apiFetch';

// ─── Topic tags ───────────────────────────────────────────────────────────────

const PRESET_TOPICS = [
  'Budget & Finance',
  'Cannabis',
  'Development',
  'Elections',
  'Environment',
  'Housing',
  'Land Use & Zoning',
  'Procurement',
  'Public Health',
  'Public Safety',
  'Transportation',
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface WizardResult {
  name: string;
  description: string;
  jurisdictionIds: string[];
  dateFrom: string | null;
  dateTo: string | null;
  topics: string[];
}

interface Props {
  onDone: (result: WizardResult) => Promise<void>;
  onCancel: () => void;
}

// ─── Progress bar ─────────────────────────────────────────────────────────────

function StepIndicator({ step }: { step: 1 | 2 | 3 }) {
  const steps = [
    { n: 1, label: 'Name' },
    { n: 2, label: 'Jurisdictions' },
    { n: 3, label: 'Focus' },
  ];
  return (
    <div className="flex items-center gap-0">
      {steps.map((s, i) => (
        <React.Fragment key={s.n}>
          <div className="flex flex-col items-center gap-1">
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${
                step > s.n
                  ? 'bg-blue-600 text-white'
                  : step === s.n
                    ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                    : 'bg-gray-100 text-gray-400'
              }`}
            >
              {step > s.n ? <Check className="h-4 w-4" /> : s.n}
            </div>
            <span
              className={`text-xs font-medium whitespace-nowrap ${
                step >= s.n ? 'text-blue-700' : 'text-gray-400'
              }`}
            >
              {s.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`mb-5 h-0.5 w-12 flex-shrink-0 transition-colors ${
                step > s.n ? 'bg-blue-600' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Step 1 — Name ────────────────────────────────────────────────────────────

function StepName({
  name,
  description,
  onChange,
  onNext,
}: {
  name: string;
  description: string;
  onChange: (field: 'name' | 'description', value: string) => void;
  onNext: () => void;
}) {
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <Label htmlFor="ws-name" className="text-sm font-medium text-gray-700">
          Workspace name <span className="text-red-500">*</span>
        </Label>
        <Input
          ref={nameRef}
          id="ws-name"
          value={name}
          onChange={(e) => onChange('name', e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && name.trim()) onNext();
          }}
          placeholder="e.g. Cannabis Regulation Investigation"
          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-blue-500"
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="ws-desc" className="text-sm font-medium text-gray-700">
          Description{' '}
          <span className="font-normal text-gray-400">(optional)</span>
        </Label>
        <Textarea
          id="ws-desc"
          value={description}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder="What are you investigating?"
          rows={2}
          className="bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus-visible:ring-blue-500 resize-none"
        />
      </div>

      <div className="flex justify-end">
        <Button
          onClick={onNext}
          disabled={!name.trim()}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function StepJurisdictions({
  searchQuery,
  searchResults,
  searchLoading,
  searchError,
  selected,
  selectedNames,
  onSearchChange,
  onToggle,
  onRemove,
  onBack,
  onNext,
}: {
  searchQuery: string;
  searchResults: { id: string; name: string; level: string; path: string }[];
  searchLoading: boolean;
  searchError: string | null;
  selected: Set<string>;
  selectedNames: Map<string, string>;
  onSearchChange: (q: string) => void;
  onToggle: (id: string, name: string) => void;
  onRemove: (id: string) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const levelIcon = (level: string) => {
    if (level === 'state')
      return <Globe className="h-3.5 w-3.5 shrink-0 text-blue-500" />;
    if (level === 'county')
      return <Building2 className="h-3.5 w-3.5 shrink-0 text-amber-500" />;
    return <MapPin className="h-3.5 w-3.5 shrink-0 text-rose-400" />;
  };

  const levelBadge = (level: string) => {
    if (level === 'state') return 'State';
    if (level === 'county') return 'County';
    return level;
  };

  return (
    <div className="space-y-4">
      {/* Selected pills */}
      {selected.size > 0 && (
        <div className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
            Selected ({selected.size})
          </p>
          <div className="flex flex-wrap gap-1.5">
            {Array.from(selected).map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-200"
              >
                {selectedNames.get(id) ?? id.slice(0, 8)}
                <button
                  type="button"
                  onClick={() => onRemove(id)}
                  className="rounded-full p-0.5 hover:bg-blue-200"
                >
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search states and counties…"
          autoFocus
          className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        />
      </div>

      {/* Results */}
      <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2">
        {searchLoading && (
          <div className="flex items-center justify-center py-12 text-sm text-gray-400">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Searching…
          </div>
        )}
        {searchError && (
          <div className="flex items-center justify-center py-12 text-sm text-red-500">
            {searchError}
          </div>
        )}

        {!searchLoading && !searchError && searchQuery.length < 2 && (
          <p className="py-6 text-center text-sm text-gray-400">
            Type to search states and counties
          </p>
        )}

        {!searchLoading &&
          !searchError &&
          searchQuery.length >= 2 &&
          searchResults.length === 0 && (
            <p className="py-4 text-center text-sm text-gray-400">
              No results for &quot;{searchQuery}&quot;
            </p>
          )}

        {!searchLoading &&
          !searchError &&
          searchResults.map((j) => {
            const isChecked = selected.has(j.id);
            return (
              <button
                key={j.id}
                type="button"
                onClick={() => onToggle(j.id, j.name)}
                className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition-colors ${
                  isChecked ? 'bg-blue-50' : 'hover:bg-gray-50'
                }`}
              >
                <div
                  className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                    isChecked
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {isChecked && <Check className="h-2.5 w-2.5 text-white" />}
                </div>
                {levelIcon(j.level)}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-800">
                    {j.name}
                  </p>
                  {j.path && (
                    <p className="truncate text-xs text-gray-400">
                      {j.path.replace(/\./g, ' › ')}
                    </p>
                  )}
                </div>
                <span className="shrink-0 text-xs text-gray-400">
                  {levelBadge(j.level)}
                </span>
              </button>
            );
          })}
      </div>

      {selected.size === 0 && (
        <p className="text-xs text-amber-600">
          Select at least one jurisdiction to continue.
        </p>
      )}

      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          className="border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onNext}
          disabled={selected.size === 0}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          Continue
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3 — Focus & Summary ─────────────────────────────────────────────────

function TopicTag({
  label,
  active,
  onToggle,
}: {
  label: string;
  active: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-medium ring-1 ring-inset transition-colors ${
        active
          ? 'bg-blue-600 text-white ring-blue-700'
          : 'bg-white text-gray-600 ring-gray-300 hover:ring-blue-400 hover:text-blue-600'
      }`}
    >
      {active && <Check className="h-3 w-3" />}
      {label}
    </button>
  );
}

function StepFocus({
  name,
  jurisdictionNames,
  dateFrom,
  dateTo,
  topics,
  customTopic,
  onDateChange,
  onTopicToggle,
  onCustomTopicChange,
  onAddCustom,
  onBack,
  onSubmit,
  submitting,
  submitError,
}: {
  name: string;
  jurisdictionNames: string[];
  dateFrom: string;
  dateTo: string;
  topics: Set<string>;
  customTopic: string;
  onDateChange: (field: 'dateFrom' | 'dateTo', value: string) => void;
  onTopicToggle: (label: string) => void;
  onCustomTopicChange: (v: string) => void;
  onAddCustom: () => void;
  onBack: () => void;
  onSubmit: () => void;
  submitting: boolean;
  submitError: string | null;
}) {
  const customRef = useRef<HTMLInputElement>(null);

  return (
    <div className="space-y-5">
      {/* Date range */}
      <div>
        <p className="mb-2 text-sm font-semibold text-gray-700">
          Date range{' '}
          <span className="font-normal text-gray-400">(optional)</span>
        </p>
        <div className="flex items-center gap-3">
          <div className="flex-1 space-y-1">
            <Label htmlFor="date-from" className="text-xs text-gray-500">
              From
            </Label>
            <Input
              id="date-from"
              type="date"
              value={dateFrom}
              onChange={(e) => onDateChange('dateFrom', e.target.value)}
              className="bg-white border-gray-300 text-gray-900"
            />
          </div>
          <span className="mt-5 text-gray-400">→</span>
          <div className="flex-1 space-y-1">
            <Label htmlFor="date-to" className="text-xs text-gray-500">
              To
            </Label>
            <Input
              id="date-to"
              type="date"
              value={dateTo}
              onChange={(e) => onDateChange('dateTo', e.target.value)}
              className="bg-white border-gray-300 text-gray-900"
            />
          </div>
        </div>
      </div>

      {/* Topic tags */}
      <div>
        <p className="mb-2 text-sm font-semibold text-gray-700">
          Topics <span className="font-normal text-gray-400">(optional)</span>
        </p>
        <div className="flex flex-wrap gap-2">
          {PRESET_TOPICS.map((t) => (
            <TopicTag
              key={t}
              label={t}
              active={topics.has(t)}
              onToggle={() => onTopicToggle(t)}
            />
          ))}
          {/* Custom topics already added */}
          {Array.from(topics)
            .filter((t) => !PRESET_TOPICS.includes(t))
            .map((t) => (
              <TopicTag
                key={t}
                label={t}
                active
                onToggle={() => onTopicToggle(t)}
              />
            ))}
        </div>

        {/* Add custom */}
        <div className="mt-2 flex items-center gap-2">
          <input
            ref={customRef}
            type="text"
            value={customTopic}
            onChange={(e) => onCustomTopicChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && customTopic.trim()) {
                e.preventDefault();
                onAddCustom();
              }
            }}
            placeholder="Add custom topic…"
            className="flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={onAddCustom}
            disabled={!customTopic.trim()}
            className="border-gray-300 text-gray-700"
          >
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {/* Summary card */}
      <div className="rounded-xl border border-blue-100 bg-blue-50 p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-blue-600">
          Workspace summary
        </p>
        <div className="space-y-2">
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-blue-200">
              <FileText className="h-3 w-3 text-blue-700" />
            </div>
            <div>
              <p className="text-sm font-semibold text-gray-900">{name}</p>
            </div>
          </div>
          <div className="flex items-start gap-2">
            <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-blue-200">
              <Globe className="h-3 w-3 text-blue-700" />
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-0.5">Jurisdictions</p>
              <div className="flex flex-wrap gap-1">
                {jurisdictionNames.slice(0, 5).map((n) => (
                  <span
                    key={n}
                    className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-700 ring-1 ring-gray-200"
                  >
                    {n}
                  </span>
                ))}
                {jurisdictionNames.length > 5 && (
                  <span className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-gray-500 ring-1 ring-gray-200">
                    +{jurisdictionNames.length - 5} more
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {submitError && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
          {submitError}
        </p>
      )}

      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={submitting}
          className="border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={submitting}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {submitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating…
            </>
          ) : (
            <>
              <Check className="mr-2 h-4 w-4" />
              Create Workspace
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// ─── Root wizard ──────────────────────────────────────────────────────────────

export const NewWorkspaceWizard: React.FC<Props> = ({ onDone, onCancel }) => {
  // ── Step state ──
  const [step, setStep] = useState<1 | 2 | 3>(1);

  // ── Step 1 state ──
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  // ── Step 2 state ──
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<
    { id: string; name: string; level: string; path: string }[]
  >([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [selectedNames, setSelectedNames] = useState<Map<string, string>>(
    new Map()
  );

  // ── Debounced search ──
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setSearchResults([]);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    try {
      const res = await apiFetch(
        `/api/jurisdictions/search?q=${encodeURIComponent(q)}&limit=20`
      );
      if (!res.ok) throw new Error('Search failed');
      const json = await res.json();
      setSearchResults(json?.data ?? []);
    } catch (err) {
      setSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setSearchLoading(false);
    }
  }, []);

  const handleSearchChange = useCallback(
    (q: string) => {
      setSearchQuery(q);
      if (searchTimer.current) clearTimeout(searchTimer.current);
      searchTimer.current = setTimeout(() => void doSearch(q), 250);
    },
    [doSearch]
  );

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, []);

  // ── Select / deselect ──
  const handleToggle = useCallback((id: string, name: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        setSelectedNames((sn) => {
          const ns = new Map(sn);
          ns.delete(id);
          return ns;
        });
      } else {
        next.add(id);
        setSelectedNames((sn) => new Map(sn).set(id, name));
      }
      return next;
    });
  }, []);

  const handleRemove = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    setSelectedNames((sn) => {
      const ns = new Map(sn);
      ns.delete(id);
      return ns;
    });
  }, []);

  const jurisdictionNames = useMemo(
    () => Array.from(selectedNames.values()),
    [selectedNames]
  );
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [topics, setTopics] = useState<Set<string>>(new Set());
  const [customTopic, setCustomTopic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Topic helpers ──
  const handleTopicToggle = useCallback((label: string) => {
    setTopics((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  }, []);

  const handleAddCustom = useCallback(() => {
    const t = customTopic.trim();
    if (!t) return;
    setTopics((prev) => new Set([...prev, t]));
    setCustomTopic('');
  }, [customTopic]);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await onDone({
        name: name.trim(),
        description: description.trim(),
        jurisdictionIds: Array.from(selected),
        dateFrom: dateFrom || null,
        dateTo: dateTo || null,
        topics: Array.from(topics),
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to create workspace'
      );
      setSubmitting(false);
    }
  }, [onDone, name, description, selected, dateFrom, dateTo, topics]);

  // ── Render ──
  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          type="button"
          onClick={onCancel}
          className="flex items-center gap-1 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          My Workspaces
        </button>
        <ChevronRight className="h-3.5 w-3.5 text-gray-300" />
        <span className="font-medium text-gray-900">New workspace</span>
      </div>

      {/* Progress */}
      <div className="flex justify-center py-2">
        <StepIndicator step={step} />
      </div>

      {/* Step content */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 1 && 'Name your workspace'}
            {step === 2 && 'Choose jurisdictions'}
            {step === 3 && 'Set focus (optional)'}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500">
            {step === 1 && 'Give this workspace a clear, descriptive name.'}
            {step === 2 &&
              'Select the jurisdictions whose documents you want to include.'}
            {step === 3 &&
              'Narrow by date range or topic, or skip and create now.'}
          </p>
        </div>

        {step === 1 && (
          <StepName
            name={name}
            description={description}
            onChange={(field, value) => {
              if (field === 'name') setName(value);
              else setDescription(value);
            }}
            onNext={() => setStep(2)}
          />
        )}

        {step === 2 && (
          <StepJurisdictions
            searchQuery={searchQuery}
            searchResults={searchResults}
            searchLoading={searchLoading}
            searchError={searchError}
            selected={selected}
            selectedNames={selectedNames}
            onSearchChange={handleSearchChange}
            onToggle={handleToggle}
            onRemove={handleRemove}
            onBack={() => setStep(1)}
            onNext={() => setStep(3)}
          />
        )}

        {step === 3 && (
          <StepFocus
            name={name}
            jurisdictionNames={jurisdictionNames}
            dateFrom={dateFrom}
            dateTo={dateTo}
            topics={topics}
            customTopic={customTopic}
            onDateChange={(field, value) => {
              if (field === 'dateFrom') setDateFrom(value);
              else setDateTo(value);
            }}
            onTopicToggle={handleTopicToggle}
            onCustomTopicChange={setCustomTopic}
            onAddCustom={handleAddCustom}
            onBack={() => setStep(2)}
            onSubmit={handleSubmit}
            submitting={submitting}
            submitError={submitError}
          />
        )}
      </div>
    </div>
  );
};
